import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { Badge } from './badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Skeleton } from './skeleton';
export function Combobox(props) {
    const { className, disabled, emptyMessage = 'Aucun résultat trouvé', isLoading = false, minSearchLength = 0, modal = false, onSearchChange, options, placeholder = 'Sélectionner...', searchHint, shouldFilter = onSearchChange ? false : undefined, } = props;
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    // Hauteur de la liste figée à l'ouverture, en mode modal seulement (Dialog). Sans
    // elle, la liste rapetisse à chaque lettre tapée : près du bas de la fenêtre, le
    // popover ouvert au-dessus du champ bascule en dessous et la recherche saute.
    const [listMinHeight, setListMinHeight] = useState();
    const measureList = useCallback((node) => {
        setListMinHeight(node?.offsetHeight);
    }, []);
    const optionByValue = useMemo(() => {
        const map = new Map();
        for (const o of options)
            map.set(o.value, o);
        return map;
    }, [options]);
    const isSelected = (value) => {
        if (props.mode === 'single')
            return props.value === value;
        return props.value.includes(value);
    };
    const handleSelect = (value) => {
        if (props.mode === 'single') {
            props.onChange(props.value === value ? undefined : value);
            setOpen(false);
            return;
        }
        const selected = props.value.includes(value)
            ? props.value.filter((v) => v !== value)
            : [...props.value, value];
        props.onChange(selected);
    };
    const removeMulti = (value, event) => {
        if (props.mode !== 'multi')
            return;
        event.preventDefault();
        event.stopPropagation();
        props.onChange(props.value.filter((v) => v !== value));
    };
    const triggerContent = (() => {
        if (props.mode === 'single') {
            const selectedOption = props.value ? optionByValue.get(props.value) : undefined;
            return (_jsx("span", { className: cn('truncate', !selectedOption && 'text-gray-500'), children: selectedOption?.label ?? placeholder }));
        }
        if (props.value.length === 0) {
            return _jsx("span", { className: "text-gray-500", children: placeholder });
        }
        return (_jsx("div", { className: "flex flex-1 flex-wrap gap-1", children: props.value.map((value) => {
                const option = optionByValue.get(value);
                return (_jsxs(Badge, { className: "flex items-center gap-1 border-blue-200 bg-blue-50 pr-1 text-blue-700 hover:bg-blue-100", variant: "outline", children: [option?.label ?? value, _jsx("button", { "aria-label": `Retirer ${option?.label ?? value}`, className: "rounded-full p-0.5 hover:bg-blue-200", onClick: (e) => removeMulti(value, e), onPointerDown: (e) => e.stopPropagation(), type: "button", children: _jsx(X, { className: "h-3 w-3" }) })] }, value));
            }) }));
    })();
    let commandListContent;
    if (isLoading) {
        commandListContent = (_jsx("div", { className: "p-1", children: _jsx(Skeleton, { className: "h-8 w-full" }) }));
    }
    else if (search.length < minSearchLength) {
        commandListContent = (_jsx("div", { className: "px-2 py-3 text-center text-sm text-gray-500", children: searchHint ??
                `Tapez au moins ${minSearchLength} caractère${minSearchLength > 1 ? 's' : ''} pour rechercher.` }));
    }
    else {
        commandListContent = (_jsxs(_Fragment, { children: [_jsx(CommandEmpty, { children: emptyMessage }), _jsx(CommandGroup, { children: options.map((option) => {
                        const selected = isSelected(option.value);
                        return (_jsxs(CommandItem, { onSelect: () => handleSelect(option.value), value: option.label, children: [_jsx(Check, { className: cn('h-4 w-4', selected ? 'opacity-100' : 'opacity-0') }), _jsx("span", { className: "flex-1", children: option.label })] }, option.value));
                    }) })] }));
    }
    return (_jsxs(Popover, { modal: modal, onOpenChange: setOpen, open: open, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { "aria-expanded": open, className: cn('border-input bg-background text-foreground focus-visible:border-ring focus-visible:ring-ring/20 inline-flex min-h-9 min-w-72 items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-left text-sm shadow-sm shadow-black/5 transition-shadow focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50', className), disabled: disabled, role: "combobox", type: "button", children: [_jsx("div", { className: "flex flex-1 items-center", children: triggerContent }), _jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-gray-500" })] }) }), _jsx(PopoverContent, { align: "start", className: "p-0", style: { width: 'var(--radix-popover-trigger-width)' }, children: _jsxs(Command, { shouldFilter: shouldFilter, children: [_jsx(CommandInput, { className: "pl-3", onValueChange: (value) => {
                                setSearch(value);
                                onSearchChange?.(value);
                            }, placeholder: "Rechercher...", value: search }), _jsx(CommandList, { ref: modal ? measureList : undefined, style: modal ? { minHeight: listMinHeight } : undefined, children: commandListContent })] }) })] }));
}
